import { observable, freeze } from 'rewire-core';
import { matchPath } from 'react-router-dom';
import {
  jobScheduleRouteManager,
  employeeScheduleRouteManager,
  timeOffCalendarRouteManager,
  timeOffListRouteManager,
  payRouteManager,
  timecardSummaryRouteManager,
  timecardTestRouteManager,
  batchRouteManager,
  adjustmentsBatchRouteManager,
  batchSummaryRouteManager,
  reportRouteManager,
  analyticsRouteManager,
} from './RouteManager';

interface INavigationTabsModelState {
  primaryIdx: number | false;
  secondaryIdx: number | false;
  getPrimaryPlanRoute: () => string;
  getPrimaryPayRoute: () => string;
  getPrimaryReportRoute: () => string;
  getPrimaryBatchRoute: () => string;
}

export class NavigationTabsModel {
  state: INavigationTabsModelState;

  constructor() {
    this.state = observable({
      primaryIdx: false,
      secondaryIdx: false,
      getPrimaryPlanRoute: this.getJobScheduleRoute,
      getPrimaryPayRoute: this.getPayRoute,
      getPrimaryBatchRoute: this.getBatchRoute,
      getPrimaryReportRoute: this.getReportRoute,
    });
  }

  get primaryIdx(): number | false {
    return this.state.primaryIdx;
  }

  set primaryIdx(value: number | false) {
    this.state.primaryIdx = value;
  }

  get secondaryIdx(): number | false {
    return this.state.secondaryIdx;
  }

  set secondaryIdx(value: number | false) {
    this.state.secondaryIdx = value;
  }

  get getPrimaryPlanRoute(): () => string {
    return this.state.getPrimaryPlanRoute;
  }

  set getPrimaryPlanRoute(value: () => string) {
    this.state.getPrimaryPlanRoute = value;
  }

  get getPrimaryPayRoute(): () => string {
    return this.state.getPrimaryPayRoute;
  }

  set getPrimaryPayRoute(value: () => string) {
    this.state.getPrimaryPayRoute = value;
  }

  set getPrimaryReportRoute(value: () => string) {
    this.state.getPrimaryReportRoute = value;
  }

  get getPrimaryReportRoute(): () => string {
    return this.state.getPrimaryReportRoute;
  }

  get getPrimaryBatchRoute(): () => string {
    return this.state.getPrimaryBatchRoute;
  }

  set getPrimaryBatchRoute(value: () => string) {
    this.state.getPrimaryBatchRoute = value;
  }

  reset() {
    freeze(() => {
      this.primaryIdx = false;
      this.secondaryIdx = false;
    });
  }

  update() {
    this.reset();
    freeze(() => {
      const newPrimaryIdx = this.getPrimaryIdxFromPath();
      const newSecondaryIdx = this.getSecondaryIdxFromPath();
      this.primaryIdx = newPrimaryIdx;
      this.secondaryIdx = newSecondaryIdx;
      this.setPrimaryRouteFn(newPrimaryIdx, newSecondaryIdx);
    });
  }

  getJobScheduleRoute(): string {
    return jobScheduleRouteManager.createPayPlanLinkFor();
  }

  getEmployeeScheduleRoute(): string {
    return employeeScheduleRouteManager.createPayPlanLinkFor();
  }

  getTimeOffCalendarRoute(): string {
    return timeOffCalendarRouteManager.createPayPlanLinkFor();
  }

  getTimeOffListRoute(): string {
    return timeOffListRouteManager.createPayPlanLinkFor();
  }

  getPayRoute(): string {
    return payRouteManager.createPayPlanLinkFor({ selectedBatchId: undefined });
  }

  getTimecardSummaryRoute(): string {
    return timecardSummaryRouteManager.createPayPlanLinkFor({ selectedBatchId: undefined });
  }

  getBatchRoute(): string {
    return batchRouteManager.createPayPlanLinkFor();
  }

  getAdjustmentsBatchRoute(): string {
    return adjustmentsBatchRouteManager.createPayPlanLinkFor();
  }

  getBatchSummaryRoute(): string {
    return batchSummaryRouteManager.createPayPlanLinkFor();
  }

  getReportRoute(): string {
    return reportRouteManager.createPayPlanLinkFor();
  }

  getAnalyticsRoute(): string {
    return analyticsRouteManager.createPayPlanLinkFor();
  }

  getTestRoute(): string {
    return timecardTestRouteManager.createPayPlanLinkFor();
  }

  getPrimaryIdxFromPath(): number | false {
    if (matchPath(location.pathname, { path: jobScheduleRouteManager.path })) return 0;
    if (matchPath(location.pathname, { path: employeeScheduleRouteManager.path })) return 0;
    if (matchPath(location.pathname, { path: timeOffCalendarRouteManager.path })) return 0;
    if (matchPath(location.pathname, { path: timeOffListRouteManager.path })) return 0;
    if (matchPath(location.pathname, { path: payRouteManager.path })) return 1;
    if (matchPath(location.pathname, { path: timecardSummaryRouteManager.path })) return 1;
    if (matchPath(location.pathname, { path: batchRouteManager.path })) return 2;
    if (matchPath(location.pathname, { path: batchSummaryRouteManager.path })) return 2;
    if (matchPath(location.pathname, { path: adjustmentsBatchRouteManager.path })) return 2;
    if (matchPath(location.pathname, { path: reportRouteManager.path })) return 3;
    if (matchPath(location.pathname, { path: timecardTestRouteManager.path })) return 4;
    return false;
  }

  getSecondaryIdxFromPath(): number | false {
    if (matchPath(location.pathname, { path: jobScheduleRouteManager.path })) return 0;
    if (matchPath(location.pathname, { path: payRouteManager.path })) return 0;
    if (matchPath(location.pathname, { path: batchRouteManager.path })) return 0;
    if (matchPath(location.pathname, { path: analyticsRouteManager.path })) return 1;
    if (matchPath(location.pathname, { path: reportRouteManager.path })) return 0;
    // if (matchPath(location.pathname, { path: timecardTestRouteManager.    path })) return 0;
    if (matchPath(location.pathname, { path: employeeScheduleRouteManager.path })) return 1;
    if (matchPath(location.pathname, { path: timecardSummaryRouteManager.path })) return 1;
    if (matchPath(location.pathname, { path: adjustmentsBatchRouteManager.path })) return 1;
    if (matchPath(location.pathname, { path: batchSummaryRouteManager.path })) return 2;
    if (matchPath(location.pathname, { path: timeOffCalendarRouteManager.path })) return 2;
    if (matchPath(location.pathname, { path: timeOffListRouteManager.path })) return 3;
    return false;
  }

  setPrimaryRouteFn(primaryIdx: number | false, secondaryIdx: number | false) {
    switch (primaryIdx) {
      case 0:
        if (secondaryIdx === 0) {
          this.getPrimaryPlanRoute = this.getJobScheduleRoute;
        } else if (secondaryIdx === 1) {
          this.getPrimaryPlanRoute = this.getEmployeeScheduleRoute;
        } else if (secondaryIdx === 2) {
          this.getPrimaryPlanRoute = this.getTimeOffCalendarRoute;
        } else if (secondaryIdx === 3) {
          this.getPrimaryPlanRoute = this.getTimeOffListRoute;
        }
        break;
      case 1:
        if (secondaryIdx === 0) {
          this.getPrimaryPayRoute = this.getPayRoute;
        } else if (secondaryIdx === 1) {
          this.getPrimaryPayRoute = this.getTimecardSummaryRoute;
        }
        break;
      case 2:
        if (secondaryIdx === 0) {
          this.getPrimaryBatchRoute = this.getBatchRoute;
        } else if (secondaryIdx === 1) {
          this.getPrimaryBatchRoute = this.getAdjustmentsBatchRoute;
        } else if (secondaryIdx === 2) {
          this.getPrimaryBatchRoute = this.getBatchSummaryRoute;
        }
        break;
      case 3:
        if (secondaryIdx === 0) {
          this.getPrimaryReportRoute = this.getReportRoute;
        } else if (secondaryIdx === 1) {
          this.getPrimaryReportRoute = this.getAnalyticsRoute;
        }
        break;
    }
  }
}

const navigationTabsModel = new NavigationTabsModel();
export default navigationTabsModel;
